/* eslint-disable no-param-reassign */
import { useCallback, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { flatten } from 'flat';
import { OptimizelyContext, useTrackEvent } from '@optimizely/react-sdk';
import { useSolutionOrg } from '../config/useSolutionOrg';
import { selectMemberInformation, selectMemberSession } from '../store/slices/onboardingSlice';
import { useAppDispatch, useAppSelector } from '../store/hooks';
// import * as thirdPartyAPI from '../api/thirdPartyAPI';
import {
  ContentEventType,
  FeedEventType,
  OnboardingEventType,
  SearchEventType,
  ServiceNationSolutionOrgName,
  SignUpEventType,
  SolutionOrgName,
} from './constants';
import {
  sendContentEventToOptimusBackendThunk,
  sendFeedEventToOptimusBackendThunk,
  sendOnboardingEventToOptimusBackendThunk,
  sendSearchEventToOptimusBackendThunk,
  sendSessionEventToOptimusBackendThunk,
} from '../store/thunks/sendEventToOptimusBackendThunk';
import {
  ContentEventDetailProps,
  CustomEventDetailProps,
  CustomEventProps,
  EngagementSubtopic,
  EventAction,
  EventObject,
  EventSubtopic,
  EventTopic,
  FeedEventDetailProps,
  LoginAction,
  LoginSubtopic,
  OptimusEvent,
  PublishEventRequest,
  RequestAction,
  RequestEventDetailProps,
  RequestSubtopic,
  SearchEventDetailProps,
  SignUpAction,
} from './EventTrackingTypes';
import { ContentInteractionAction } from './models/content/Content';
import { Logger } from '../config/logging/logger';
import { useChecklistSteps } from '../components/Dashboard/Home/ChecklistComponent';
import { useUpdateChecklistCompletedStepsByKey } from '../components/Dashboard/Home/useUpdateChecklistCompletedStepsByKey';
import { isOnboardingFlowTestEligibleSolutionOrg } from './hooks/useOnboardingFlowTest';
import { extractCategories } from './utils/contentUtils';
import { resolveHeapPlan } from './models/Subscription';

const PARTNER_ENROLLMENT_SUBMIT_FORM_EVENT = 'Partner-Enrollment-Submit-Form';
const ONBOARDING_FLOW_TEST_PARTNER_ENROLLMENT_SUBMIT_FORM_EVENT =
  'Partner-Enrollment-Submit-OnboardingFlowTestForm';

export const useCustomEventTracking = () => {
  const { solutionOrgName, isNativeAppLoginExperience } = useSolutionOrg();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const {
    memberId: storeMemberId, solutionOrgId, sessionId,
    memberDeviceType: storeMemberDeviceType, memberDeviceVersion: storeMemberDeviceVersion,
  } =
    useAppSelector(selectMemberSession);
  const memberId = storeMemberId || searchParams.get('memberId');
  const memberDeviceType = storeMemberDeviceType || searchParams.get('deviceType');
  const memberDeviceVersion = storeMemberDeviceVersion || searchParams.get('deviceVersion');
  const {
    id: memberInformationId, companyName, firstName, lastName, email, company,
  } = useAppSelector(selectMemberInformation);
  const heapPlan = resolveHeapPlan({
    company,
    isServiceNation: solutionOrgName === ServiceNationSolutionOrgName,
  });
  const { optimizely } = useContext(OptimizelyContext);
  const [track, ClientReady] = useTrackEvent();
  const logger = Logger.create('useCustomEventTracking');
  const checklistSteps = useChecklistSteps();
  const updateChecklistCompletedStepsByKey = useUpdateChecklistCompletedStepsByKey();

  // Publish events to analytics providers like heap, google analytics, our BE
  const publishToAnalyticsProviders = useCallback(
    ({
      optimusEventType,
      optimusEvents,
      solutionOrg,
      apiResponseErrorStatus,
    }: PublishEventRequest) => {
      // Do not send error events for authentication errors
      if (apiResponseErrorStatus && (apiResponseErrorStatus === 401 ||
        apiResponseErrorStatus === 403)) {
        return;
      }
      optimusEvents.forEach(async (optimusEvent) => {
        // fire event to Google Analytics (google analytics
        // convention for event names has underscore)
        // Google analytics only supports sending one event at a time
        ReactGA.gtag('event', optimusEvent.name.replace(/-/g, '_'), flatten(optimusEvent.detail, { delimiter: '_' }));

        // replace '_' with '-'
        optimusEvent.name = optimusEvent.name.replace(/_/g, '-');

        // fire non-Error events to Heap staging or prod
        const errorEventRegex = /^Request-Error/;
        const heapPayload = flatten(optimusEvent.detail, { delimiter: '-' }) as { [key: string]: string | boolean | number };
        if (!errorEventRegex.test(optimusEvent.name)) {
          logger.debug(Logger.format('useCustomEventTracking:heap:', optimusEvent.name, heapPayload));
          if (global.heap) {
            try {
              global.heap.track(optimusEvent.name, heapPayload);
            } catch (err) {
              logger.error(Logger.format(err));
            }
          }
        }

        // Commenting this out in case we ever need it as a temporary workaround for heap tracking
        // fire non-Error events to Heap staging or prod
        /* const errorEventRegex = /^Request-Error/;
        if (!errorEventRegex.test(optimusEvent.name) &&
          (process.env.REACT_APP_ENVIRONMENT === 'staging' ||
            process.env.REACT_APP_ENVIRONMENT === 'prod'
          )
        ) {
          try {
            await thirdPartyAPI.heapTrack({
              app_id: process.env.REACT_APP_HEAP_SCRIPT_ID || '',
              identity: optimusEvent?.detail?.memberId as string || '',
              event: optimusEvent.name,
              properties: flatten(optimusEvent.detail, { delimiter: '-' }),
            });
          } catch (err) {
            console.log(err);
          }
        } */

        // fire events to optimizely
        if (optimusEvent.detail?.isOptimizelyEvent) {
          logger.debug(Logger.format(
            `optimizely user context: ${JSON.stringify(optimizely || {})}`,
          ));
          if (ClientReady && optimizely?.user.id) {
            const optimizelyUserId = optimizely.user.id;
            const optimizelyAttributes = { ...(optimizely.user.attributes ?? {}) };
            delete optimizelyAttributes.email;

            const shouldTrackOnboardingFlowTestSubmit =
              optimusEvent.name === PARTNER_ENROLLMENT_SUBMIT_FORM_EVENT &&
              isOnboardingFlowTestEligibleSolutionOrg(solutionOrgName as string);
            const optimizelyEventNames = shouldTrackOnboardingFlowTestSubmit ? [
              optimusEvent.name,
              ONBOARDING_FLOW_TEST_PARTNER_ENROLLMENT_SUBMIT_FORM_EVENT,
            ] : [optimusEvent.name];

            optimizelyEventNames.forEach((eventName) => {
              track(
                eventName,
                optimizelyUserId,
                optimizelyAttributes,
              );
            });
          }
        }
      });

      // fire events to optimus backend
      switch (optimusEventType) {
        case OnboardingEventType:
          dispatch(sendOnboardingEventToOptimusBackendThunk({
            optimusEvents,
            solutionOrg,
          }));
          break;
        case ContentEventType:
          dispatch(sendContentEventToOptimusBackendThunk(optimusEvents));
          break;
        case FeedEventType:
          dispatch(sendFeedEventToOptimusBackendThunk(optimusEvents));
          break;
        case SearchEventType:
          dispatch(sendSearchEventToOptimusBackendThunk(optimusEvents));
          break;
        default:
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ClientReady, track],
  );

  // Generic tracking function
  const trackCustomEvent = useCallback(
    (
      topic: EventTopic,
      subtopic: EventSubtopic,
      action: EventAction,
      object: EventObject,
      customEventProps: CustomEventProps,
    ) => {
      const { optimusEventType, apiResponseErrorStatus, ...otherProps } = customEventProps;
      const name = `${topic}-${subtopic}-${action}-${object}`;

      const optimusEvent: OptimusEvent = {
        name,
        detail: {
          eventTopic: topic,
          eventSubtopic: subtopic,
          memberId,
          optimusSessionId: sessionId,
          solutionOrgId,
          ...(firstName && { firstName }),
          ...(lastName && { lastName }),
          ...(email && { email }),
          ...(companyName && { companyName }),
          ...(company?.id && { companyId: company.id }),
          solutionOrgName: solutionOrgName as SolutionOrgName,
          ...(process.env.REACT_APP_GIT_SHA && { version: process.env.REACT_APP_GIT_SHA }),
          memberDeviceType,
          memberDeviceVersion,
          ...(otherProps?.detail ?? {}),
          pageLocation: window?.location?.pathname || 'unknown',
        },
      };

      // Plan is also set globally via heap.addEventProperties once the member is
      // identified; sent here too for events fired before that (e.g. onboarding).
      // Only when the member information belongs to the event's member: callers
      // can override memberId (e.g. the login event) while persisted information
      // still describes the previous member
      if (heapPlan && memberInformationId &&
        optimusEvent.detail?.memberId === memberInformationId) {
        optimusEvent.detail = {
          ...optimusEvent.detail,
          Plan: heapPlan,
        };
      }

      // Add action if it doesn't already exist
      if (!optimusEvent.detail?.action) {
        optimusEvent.detail = {
          ...optimusEvent.detail,
          action,
        };
      }

      // send event to analytics provider (e.g., Heap, google analytics, BE)
      publishToAnalyticsProviders({
        optimusEventType,
        apiResponseErrorStatus,
        optimusEvents: [optimusEvent],
        solutionOrg: solutionOrgName as SolutionOrgName,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [publishToAnalyticsProviders, memberId, memberDeviceType, heapPlan, memberInformationId],
  );

  // Specialized tracking functions
  // Track edge Enrollment events
  const trackEdgeEnrollment = useCallback(
    (
      action: EventAction,
      object: EventObject,
      customEventDetailProps: CustomEventDetailProps = {},
    ) => {
      trackCustomEvent('Edge', 'Enrollment', action, object, {
        optimusEventType: OnboardingEventType,
        detail: { ...customEventDetailProps },
      });
    },
    [trackCustomEvent],
  );

  // Track partner enrollment events
  const trackPartnerEnrollment = useCallback(
    (
      action: EventAction,
      object: EventObject,
      customEventDetailProps: CustomEventDetailProps = {},
    ) => {
      trackCustomEvent(
        'Partner',
        'Enrollment',
        action,
        object,
        {
          optimusEventType: OnboardingEventType,
          detail: { ...customEventDetailProps },
        },
      );
    },
    [trackCustomEvent],
  );

  // Track Home Depot enrollment start
  const trackHomeDepotEnrollmentStart = useCallback(
    (
      customEventDetailProps: CustomEventDetailProps = {},
    ) => {
      trackPartnerEnrollment('Open', 'HomeDepotSetup', customEventDetailProps);
    },
    [trackPartnerEnrollment],
  );

  // Track partner detail events
  const trackPartnerDetail = useCallback(
    (
      action: EventAction,
      object: EventObject,
      customEventDetailProps: CustomEventDetailProps = {},
    ) => {
      trackCustomEvent(
        'Partner',
        'Detail',
        action,
        object,
        {
          optimusEventType: OnboardingEventType,
          detail: { ...customEventDetailProps },
        },
      );
    },
    [trackCustomEvent],
  );

  // Track engagement events
  const trackEngagement = useCallback(
    (
      subtopic: EngagementSubtopic,
      action: EventAction,
      object: EventObject,
      customEventDetailProps: CustomEventDetailProps = {},
    ) => {
      trackCustomEvent(
        'Engagement',
        subtopic,
        action,
        object,
        {
          optimusEventType: OnboardingEventType,
          detail: { ...customEventDetailProps },
        },
      );
    },
    [trackCustomEvent],
  );

  // Track Payout Setting events
  const trackPayoutSetting = useCallback(
    (
      action: EventAction,
      object: EventObject,
      customEventDetailProps: CustomEventDetailProps = {},
    ) => {
      trackCustomEvent(
        'Payout',
        'Setting',
        action,
        object,
        {
          optimusEventType: OnboardingEventType,
          detail: { ...customEventDetailProps },
        },
      );
    },
    [trackCustomEvent],
  );

  // Track Account Setting events
  const trackAccountSetting = useCallback(
    (
      action: EventAction,
      object: EventObject,
      customEventDetailProps: CustomEventDetailProps = {},
    ) => {
      trackCustomEvent(
        'Account',
        'Setting',
        action,
        object,
        {
          optimusEventType: OnboardingEventType,
          detail: { ...customEventDetailProps },
        },
      );
    },
    [trackCustomEvent],
  );

  // Content interaction tracking (extracts categories automatically)
  const trackContentInteraction = useCallback(
    (
      action: ContentInteractionAction,
      object: EventObject,
      contentEventDetailProps: ContentEventDetailProps,
    ) => {
      const { contentMetaData, ...otherDetailProps } = contentEventDetailProps;

      let categories;
      if (contentMetaData?.length) {
        categories = extractCategories(contentMetaData);
      }

      // onboarding checklist last step
      if (action !== ContentInteractionAction.View) {
        // track step3 completed event
        updateChecklistCompletedStepsByKey(checklistSteps[2].key);
      }

      trackCustomEvent('Content', 'Interaction', action, object, {
        optimusEventType: ContentEventType,
        detail: {
          ...(categories || {}),
          ...otherDetailProps,
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trackCustomEvent],
  );

  // Track feed interaction
  const trackFeedInteraction = useCallback(
    (
      action: EventAction,
      object: EventObject,
      feedEventDetailProps: FeedEventDetailProps,
    ) => {
      // onboarding checklist last step
      if (action === 'Click') {
        // track step3 completed event
        updateChecklistCompletedStepsByKey(checklistSteps[2].key);
      }

      trackCustomEvent(
        'Feed',
        'Interaction',
        action,
        object,
        {
          optimusEventType: FeedEventType,
          detail: { ...feedEventDetailProps },
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trackCustomEvent],
  );

  // Track search interaction
  const trackSearchInteraction = useCallback(
    (
      action: EventAction,
      object: EventObject,
      searchEventDetailProps: SearchEventDetailProps,
    ) => {
      trackCustomEvent(
        'Search',
        'Interaction',
        action,
        object,
        {
          optimusEventType: SearchEventType,
          detail: { ...searchEventDetailProps },
        },
      );
    },
    [trackCustomEvent],
  );

  // Track Request events
  const trackRequest = useCallback(
    (
      subtopic: RequestSubtopic,
      action: RequestAction,
      object: EventObject,
      requestEventDetailProps: RequestEventDetailProps = {},
    ) => {
      const { apiResponseErrorStatus, ...otherRequestEventDetailsProp } = requestEventDetailProps;
      trackCustomEvent(
        'Request',
        subtopic,
        action,
        object,
        {
          optimusEventType: OnboardingEventType,
          ...(apiResponseErrorStatus && { apiResponseErrorStatus }),
          detail: { ...otherRequestEventDetailsProp },
        },
      );
    },
    [trackCustomEvent],
  );

  // track interaction on the Joist account page
  const trackEdgeSolutionOrgHighlight = useCallback(
    (
      action: EventAction,
      object: EventObject,
      customEventDetailProps: CustomEventDetailProps = {},
    ) => {
      trackCustomEvent('Edge', 'SolutionOrgHighlight', action, object, {
        optimusEventType: ContentEventType,
        detail: { ...customEventDetailProps },
      });
    },
    [trackCustomEvent],
  );

  // Track login events
  const trackLogin = useCallback(
    (
      subtopic: LoginSubtopic,
      action: LoginAction,
      object: EventObject,
      customEventDetailProps: CustomEventDetailProps = {},
    ) => {
      trackCustomEvent(
        'Edge',
        subtopic,
        action,
        object,
        {
          optimusEventType: OnboardingEventType,
          detail: {
            ...customEventDetailProps,
            loginExperience: isNativeAppLoginExperience ? 'nativeApp' : 'web',
            isNativeAppLoginExperience: !!isNativeAppLoginExperience,
          },
        },
      );
    },
    [isNativeAppLoginExperience, trackCustomEvent],
  );

  // Track signUp events
  const trackSignUp = useCallback(
    (
      subtopic: LoginSubtopic,
      action: SignUpAction,
      object: EventObject,
      customEventDetailProps: CustomEventDetailProps = {},
    ) => {
      trackCustomEvent(
        'Edge',
        subtopic,
        action,
        object,
        {
          optimusEventType: SignUpEventType,
          detail: { ...customEventDetailProps },
        },
      );
    },
    [trackCustomEvent],
  );

  // Track session started events
  const trackSessionStarted = useCallback(
    (
      customEventDetailProps: CustomEventDetailProps = {},
    ) => {
      // Send directly to backend with correct event name to match MemberSessionStartedEvent
      const optimusEvent: OptimusEvent = {
        name: 'MemberSessionStartedEvent',
        detail: {
          memberId,
          ...customEventDetailProps,
        },
      };

      dispatch(sendSessionEventToOptimusBackendThunk([optimusEvent]));
    },
    [dispatch, memberId],
  );

  return {
    trackCustomEvent,
    trackEdgeEnrollment,
    trackPartnerEnrollment,
    trackHomeDepotEnrollmentStart,
    trackEngagement,
    trackEdgeSolutionOrgHighlight,
    trackContentInteraction,
    trackFeedInteraction,
    trackSearchInteraction,
    trackPayoutSetting,
    trackRequest,
    trackAccountSetting,
    trackPartnerDetail,
    trackLogin,
    trackSignUp,
    trackSessionStarted,
  };
};
